import { z } from 'zod';

/**
 * Transport payload encryption (production: PAYLOAD_ENCRYPTION=true).
 *
 * A browser cannot hold a pre-shared secret, so the client and API agree on a
 * per-session AES-256-GCM key via an ECDH (P-256) handshake + HKDF-SHA256.
 * The server returns the derived key wrapped under its master key as an opaque
 * `kid` — the client echoes it in `x-payload-kid` on every request, keeping
 * the server stateless. Bodies then travel as `{ __sealed: { iv, ct, tag } }`.
 *
 * This layers on top of TLS (a common government VAPT requirement); it is not
 * a replacement for it.
 */
const base64urlSchema = z.string().regex(/^[A-Za-z0-9_-]+$/);

export const cryptoHandshakeSchema = z.object({
  clientPublicJwk: z.object({
    kty: z.literal('EC'),
    crv: z.literal('P-256'),
    x: base64urlSchema.max(64),
    y: base64urlSchema.max(64),
  }),
});
export type CryptoHandshakeInput = z.infer<typeof cryptoHandshakeSchema>;

export interface CryptoHandshakeResponseDto {
  /** Opaque wrapped session key — echo verbatim in the `x-payload-kid` header. */
  kid: string;
  serverPublicJwk: { kty: 'EC'; crv: 'P-256'; x: string; y: string };
  /** Base64 HKDF salt (16 bytes). */
  salt: string;
  /** Seconds until the kid expires and a fresh handshake is required. */
  expiresInSec: number;
}

/** HKDF `info` binding — must match on both sides of the handshake. */
export const PAYLOAD_HKDF_INFO = 'fms-payload-v1';

/** Wire shape of an encrypted request/response body (all parts base64). */
export interface SealedPayload {
  __sealed: { iv: string; ct: string; tag: string };
}

export function isSealedPayload(value: unknown): value is SealedPayload {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as SealedPayload).__sealed === 'object' &&
    (value as SealedPayload).__sealed !== null &&
    typeof (value as SealedPayload).__sealed.ct === 'string'
  );
}
